import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import {
  useGetAggregateScoresQuery,
  AggregateScoreAggregationType,
  AggregateScoreOrderField,
  AggregateScoreType,
  OrderDirection,
} from "~/operations";
import { SORT_FIELD_MAP, BaseScopeNode } from "../columns";

export type ScopeType = "Organizations" | "Spaces" | "Workspaces";

const DEFAULT_PAGE_SIZE = 10;

const TAG_NUM_CHILDREN = "mondoo.com/internal/num-children";

export type EntityScopeFilterVariables = {
  queryTerms?: string[];
  owners?: string[];
};

export interface UseEntityScopesParams {
  entityMrn: string;
  findingMrn: string | undefined;
  scopeType: ScopeType;
  filterVariables: EntityScopeFilterVariables;
  scoreType?: AggregateScoreType;
}

export interface UseEntityScopesResult<T extends BaseScopeNode> {
  data: T[];
  loading: boolean;
  error: Error | undefined;
  totalCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  rowCount: number;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
}

export function useEntityScopes<T extends BaseScopeNode = BaseScopeNode>({
  entityMrn,
  findingMrn,
  scopeType,
  filterVariables,
  scoreType,
}: UseEntityScopesParams): UseEntityScopesResult<T> {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "name", sort: "asc" },
  ]);

  // Cache cursors for each page (maps page number to endCursor)
  const [cursorCache, setCursorCache] = useState<Map<number, string>>(
    new Map(),
  );

  // Get cursor for current page (use previous page's endCursor)
  const after =
    paginationModel.page > 0
      ? cursorCache.get(paginationModel.page - 1)
      : undefined;

  // Convert DataGrid sort model to GraphQL orderBy
  const orderBy = useMemo(() => {
    if (sortModel.length === 0) {
      return {
        field: AggregateScoreOrderField.Title,
        direction: OrderDirection.Asc,
      };
    }
    const { field, sort } = sortModel[0];
    return {
      field: SORT_FIELD_MAP[field] || AggregateScoreOrderField.Title,
      direction: sort === "desc" ? OrderDirection.Desc : OrderDirection.Asc,
    };
  }, [sortModel]);

  // Map scope type to aggregation type
  const aggregationType = useMemo(() => {
    switch (scopeType) {
      case "Organizations":
        return AggregateScoreAggregationType.Organization;
      case "Spaces":
        return AggregateScoreAggregationType.Space;
      case "Workspaces":
        return AggregateScoreAggregationType.Workspace;
      default:
        return AggregateScoreAggregationType.Organization;
    }
  }, [scopeType]);

  const { data, loading, error } = useGetAggregateScoresQuery({
    variables: {
      entityMrn,
      filter: {
        ...filterVariables,
        findingMrn: findingMrn || "",
        aggregationType,
        ...(scoreType && { scoreType }),
      },
      first: paginationModel.pageSize,
      after,
      orderBy,
      skipCases: true,
    },
    skip: !findingMrn,
  });

  const aggregateScores =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores
      : undefined;

  const pageInfo = aggregateScores?.pageInfo;

  // IMPORTANT: Do not remove this memoization pattern!
  // Memoize rowCount to prevent it from resetting to 0 during loading,
  // which causes MUI DataGrid to reset pagination to page 0.
  // See: https://mui.com/x/react-data-grid/pagination/#server-side-pagination
  const rowCountRef = useRef(0);
  const rowCount = useMemo(() => {
    const newCount = aggregateScores?.totalCount;
    if (newCount !== undefined) {
      rowCountRef.current = newCount;
    }
    return rowCountRef.current;
  }, [aggregateScores?.totalCount]);

  const totalCount = aggregateScores?.totalCount || 0;

  // Cache endCursor for current page when data arrives
  useEffect(() => {
    if (pageInfo?.endCursor) {
      setCursorCache((prev) => {
        const next = new Map(prev);
        next.set(paginationModel.page, pageInfo.endCursor);
        return next;
      });
    }
  }, [pageInfo?.endCursor, paginationModel.page]);

  // Reset cursor cache and go to first page when filters, sort, or scope changes
  useEffect(() => {
    setCursorCache(new Map());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filterVariables, scopeType, orderBy.field, orderBy.direction]);

  // Pagination model change handler
  const onPaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      // If page size changed, reset to first page and clear cache
      if (newModel.pageSize !== paginationModel.pageSize) {
        setCursorCache(new Map());
        setPaginationModel({ page: 0, pageSize: newModel.pageSize });
      } else {
        setPaginationModel(newModel);
      }
    },
    [paginationModel.pageSize],
  );

  const onSortModelChange = useCallback((newModel: GridSortModel) => {
    setSortModel(newModel);
  }, []);

  const mappedData = useMemo(() => {
    if (
      !data?.aggregateScores ||
      data.aggregateScores.__typename !== "AggregateScoresConnection"
    ) {
      return [];
    }

    return (
      data.aggregateScores.edges
        ?.map((edge) => {
          const node = edge.node;
          if (!node) return null;

          const entity = node.entity as any;
          let name = entity?.name || entity?.id || node.title || "";
          let entityId = entity?.id || node.id; // Use entity ID for navigation, fallback to node ID
          let assets = node.blastRadius?.assets || 0;
          let owners: string[] = [];
          let organizationId: string | undefined;
          let organizationName: string | undefined;
          let spaceName: string | undefined;

          const numChildrenTag = node.tags?.find(
            (tag) => tag?.key === TAG_NUM_CHILDREN,
          );
          const spacesCount = numChildrenTag?.value
            ? parseInt(numChildrenTag.value, 10)
            : 0;

          let contacts: Array<{
            contactType: string;
            identity: string;
            email?: string | null;
            name?: string | null;
          }> = [];

          if (entity) {
            if (entity.__typename === "EntityInfoOrganization") {
              organizationId = entity.id;
              organizationName = entity.name;
            } else if (entity.__typename === "EntityInfoSpace") {
              spaceName = entity.name;
            }
            contacts = entity.contacts ?? [];
          }

          return {
            ...node,
            id: entityId, // Override node.id with entity.id for navigation
            name,
            assets,
            spacesCount,
            owners,
            contacts,
            organizationId,
            organizationName,
            spaceName,
          } as unknown as T;
        })
        .filter((node): node is T => node !== null) || []
    );
  }, [data]);

  return {
    data: mappedData,
    loading,
    error,
    totalCount,
    paginationModel,
    onPaginationModelChange,
    rowCount,
    sortModel,
    onSortModelChange,
  };
}
